import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Layers, ShieldCheck, FileCheck2, Sparkles, BarChart3,
  Lock, Activity, CheckCircle2, Workflow, Play, Brain, Zap, Target, Calendar
} from 'lucide-react';
import ComparisonTable from '../components/ComparisonTable';
import ImagePlaceholder from '../components/ImagePlaceholder';

const stages = [
  { name: 'Initiate',  desc: 'Strategic intake, business case, governance set-up' },
  { name: 'Plan',      desc: 'Scope, programme, commercial frame, risk register' },
  { name: 'Deliver',   desc: 'Execute against gated stages with embedded assurance' },
  { name: 'Handover',  desc: 'Commissioning, defects, asset-data capture, sign-off' },
  { name: 'Operate',   desc: 'Lifecycle planning, compliance, statutory maintenance' },
];

const outcomes = [
  { icon: ShieldCheck, title: 'Delivery Governance', desc: 'A unified, stage-gated framework. Roles, approvals and gates defined.' },
  { icon: Activity,    title: 'Assurance',            desc: 'Technical, commercial, safety and quality assurance — audit-ready by design.' },
  { icon: BarChart3,   title: 'Commercial Control',   desc: 'Procurement governance, variation uplift logic, cost assurance.' },
  { icon: Sparkles,    title: 'AI Insights',          desc: 'Narrative reporting, risk detection and decision-ready summaries.' },
  { icon: FileCheck2,  title: 'Evidence Packs',       desc: 'Structured, auto-compiled evidence that withstands audit and scrutiny.' },
  { icon: Workflow,    title: 'Integrations',         desc: 'Coordinates PM tools, design platforms, CAFM systems and document repositories.' },
];

const capabilities = [
  { icon: Layers,      code: 'IMG-010', title: 'Stage-Gated Delivery',          desc: 'A structured, evidence-driven pathway from initiation to handover. Every gate has clear entry, exit and assurance criteria.' },
  { icon: ShieldCheck, code: 'IMG-011', title: 'Governance & Assurance',        desc: 'Embedded technical, commercial, safety and quality assurance — not as add-ons, but as the spine of every workflow.' },
  { icon: FileCheck2,  code: 'IMG-012', title: 'Smart Templates & Evidence',    desc: 'Structured templates replace PDFs. Evidence packs auto-compile, ensuring consistency and auditability.' },
  { icon: Sparkles,    code: 'IMG-013', title: 'AI Intelligence Engine',         desc: 'Automated insights, narrative reporting, risk detection and evidence generation.' },
  { icon: BarChart3,   code: 'IMG-014', title: 'Commercial & Financial Control', desc: 'Procurement governance, variation uplift logic, cost assurance and commercial stage gates.' },
  { icon: Lock,        code: 'IMG-015', title: 'Safety, Quality & Compliance',   desc: 'Integrated frameworks aligned to regulatory and contractual requirements — CDM, Building Safety Act 2022, RIBA.' },
  { icon: Activity,    code: 'IMG-016', title: 'Portfolio-Wide Visibility',      desc: 'Real-time dashboards and executive reporting across all projects, assets and services.' },
];

const pillars = [
  {
    eyebrow: 'Governance',
    code: 'IMG-006',
    title: 'Structured, stage-gated, audit-ready.',
    desc: 'Every project follows the same disciplined pathway. Every gate has clear entry, exit and assurance criteria. Every approval is logged. Nothing slips through the cracks.',
    proof: ['5-stage RIBA-aligned lifecycle', 'Role-based approvals', 'Auditable decision trail'],
  },
  {
    eyebrow: 'Intelligence',
    code: 'IMG-007',
    title: 'AI that turns activity into insight.',
    desc: 'Raw delivery data becomes board-ready narrative reports. Risks are flagged before they become issues. Variance is explained, not just displayed.',
    proof: ['AI executive summaries', 'Predictive risk detection', 'Narrative reporting in seconds'],
  },
  {
    eyebrow: 'Evidence',
    code: 'IMG-008',
    title: 'Audit-ready by design, not by exception.',
    desc: 'Structured templates replace ad-hoc PDFs. Evidence packs auto-compile from real workflow activity. Audit trail is the by-product of doing the work — not a separate exercise.',
    proof: ['Auto-compiled evidence packs', 'Statutory compliance built-in', 'CDM 2015 / Building Safety Act 2022'],
  },
];

const timeline = [
  {
    label: 'Day 1',
    title: 'Live access. Sample data.',
    desc: 'Sign up to the 14-day trial. Read-only access to a sector-shaped demo plus a private sandbox where your team can build up to 3 of their own sites.',
    items: ['NHS / FM / Construction demo workspaces', 'Browse projects, gates, evidence', 'Test the AI executive summary'],
  },
  {
    label: 'Day 30',
    title: 'Embedded methodology.',
    desc: 'With Keystone\'s support, your stage gates, templates and evidence requirements are configured for your organisation\'s governance.',
    items: ['Custom stage-gate framework', 'Smart templates for your workflows', 'Role-based approvals mapped', 'First evidence packs auto-compiling'],
  },
  {
    label: 'Day 90',
    title: 'Operating at full visibility.',
    desc: 'AI executive summaries to the board. Portfolio-wide RAG rollup. Statutory compliance evidence on tap. Your delivery is governed, intelligent and audit-ready.',
    items: ['Board packs in minutes', 'Real-time portfolio RAG', 'Statutory compliance dashboards', 'Audit trail by design'],
  },
];

const V2Platform = () => (
  <div data-testid="v2-platform">

    {/* ============= HERO ============= */}
    <section className="relative pt-20 pb-20 md:pt-28 md:pb-28 overflow-hidden" data-testid="v2-platform-hero">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)',
        backgroundSize: '24px 24px'
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
              Videra · Keystone&apos;s flagship platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.04]">
              The operating system for <span className="text-blue-600">delivery.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed">
              Videra unifies stage-gated delivery, governance, commercial control, safety, AI insights
              and audit-ready evidence into one platform. Built from real operational experience to
              make the complicated simple.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="https://demo.videra.app"
                data-testid="v2-platform-cta-trial"
                className="inline-flex items-center justify-center bg-slate-900 text-white px-8 py-4 font-medium tracking-wide hover:bg-slate-800 transition-all rounded-md group"
              >
                <Sparkles className="mr-2 w-4 h-4" /> Start 14-day trial
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                data-testid="v2-platform-cta-demo"
                className="inline-flex items-center justify-center bg-white border border-slate-300 text-slate-900 px-8 py-4 font-medium tracking-wide hover:border-slate-900 transition-all rounded-md"
              >
                <Play className="mr-2 w-4 h-4" /> Book a guided demo
              </Link>
            </div>

            <p className="mt-4 text-sm text-slate-500">
              No card required · build up to 3 of your own sites · ↗ Opens in a new tab
            </p>
          </motion.div>

          {/* Right: dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-6"
          >
            <div className="relative bg-slate-900 rounded-2xl p-3 shadow-2xl border border-slate-800">
              <div className="absolute top-5 left-5 flex gap-1.5 z-10">
                <span className="w-3 h-3 rounded-full bg-red-400/70" />
                <span className="w-3 h-3 rounded-full bg-amber-400/70" />
                <span className="w-3 h-3 rounded-full bg-emerald-400/70" />
              </div>
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-slate-800 text-slate-400 text-xs px-3 py-1 rounded-md hidden sm:block">
                demo.videra.app
              </div>

              <div className="bg-slate-50 rounded-lg pt-10 overflow-hidden">
                <img
                  src="/images/construction/cp-portfolio-pmo.jpg"
                  alt="Videra PMO Command Centre — portfolio governance, RAG, and stage tracking"
                  className="w-full aspect-[16/10] object-cover object-top"
                />
              </div>

              {/* Floating accent screenshot — top-right */}
              <div className="hidden md:block absolute -top-6 -right-6 w-44 lg:w-52 bg-white rounded-xl p-1.5 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.45)] border border-slate-200 rotate-[3deg]">
                <img
                  src="/images/healthcare/hc-portfolio-tracker-hero.jpg"
                  alt="Videra Portfolio Tracker"
                  className="w-full aspect-[16/10] object-cover object-top rounded-md"
                />
              </div>
              {/* Floating accent screenshot — bottom-left */}
              <div className="hidden md:block absolute -bottom-6 -left-6 w-40 lg:w-48 bg-white rounded-xl p-1.5 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.45)] border border-slate-200 rotate-[-3deg]">
                <img
                  src="/images/nhs/nhs-board-pack.jpg"
                  alt="Videra Board Pack Report"
                  className="w-full aspect-[16/10] object-cover object-top rounded-md"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* ============= WHAT VIDERA IS — concise definition ============= */}
    <section className="py-16 md:py-20 bg-white border-y border-slate-200" data-testid="v2-platform-definition">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start">
          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold">What Videra is</p>
          </div>
          <div className="md:col-span-9 space-y-5 border-l-2 border-blue-500 pl-6 md:pl-8">
            <p className="text-lg md:text-xl lg:text-2xl text-slate-900 font-medium leading-snug tracking-tight">
              Videra is a digital delivery, governance, and operational control platform designed
              for environments such as <span className="text-blue-600">healthcare, construction, estates, and public-sector programme delivery</span>.
            </p>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              It provides mapped workflows, audit-ready controls, risk visibility, and real-time operational assurance.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ============= WHY VIDERA — 3 PILLARS NARRATIVE ============= */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-platform-pillars">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Why Videra</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
            Most platforms digitise tasks.<br />
            <span className="text-slate-400">Videra defines how delivery should happen.</span>
          </h2>
        </div>

        <div className="space-y-6">
          {pillars.map((p, i) => (
            <motion.div
              key={p.eyebrow}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-white border border-slate-200 rounded-2xl p-8 md:p-10"
            >
              <div className="lg:col-span-5">
                <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
                  0{i+1} · {p.eyebrow}
                </p>
                <h3 className="text-2xl md:text-3xl font-medium text-slate-900 leading-tight">{p.title}</h3>
                <p className="mt-5 text-slate-600 leading-relaxed">{p.desc}</p>
                <ul className="mt-6 space-y-2">
                  {p.proof.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-7">
                <ImagePlaceholder
                  code={p.code}
                  ext="jpg"
                  alt={`${p.eyebrow} — Videra Healthcare`}
                  aspect="16 / 10"
                  brief={`${p.eyebrow} pillar — ${p.title}`}
                  spec="1600×1000 · JPG"
                  rounded="rounded-xl"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ============= STAGE-GATED LIFECYCLE (existing) ============= */}
    <section className="py-16 md:py-24 bg-slate-900 text-white" data-testid="v2-platform-lifecycle">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Stage-gated lifecycle</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mb-12 max-w-3xl">
          Initiation to lifecycle — one governed pathway.
        </h2>

        <div className="relative">
          <div className="hidden md:block absolute top-10 left-[7%] right-[7%] h-px bg-gradient-to-r from-transparent via-blue-500/60 to-transparent" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {stages.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative bg-slate-800/60 border border-slate-700 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 font-medium text-sm">
                    0{i+1}
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                </div>
                <h3 className="text-lg font-medium text-white mb-1">{s.name}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
                <div className="mt-4 flex gap-1.5 flex-wrap">
                  <span className="text-[10px] text-blue-300 bg-blue-500/10 px-2 py-0.5 rounded">Governance</span>
                  <span className="text-[10px] text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded">Evidence</span>
                  <span className="text-[10px] text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded">Assurance</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ============= AI INTELLIGENCE ENGINE SPOTLIGHT ============= */}
    <section className="py-20 md:py-28" data-testid="v2-platform-ai">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> AI Intelligence Engine
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
              Activity becomes <span className="text-blue-600">insight.</span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Built directly into Videra. The AI doesn&apos;t just visualise your data — it reads it, explains
              it, and flags what matters. Board-ready summaries in seconds, not days.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Brain, t: 'Narrative reporting',  d: 'Plain-English summaries written from real project data — risks called out, variance explained.' },
                { icon: Zap,   t: 'Predictive risk',       d: 'Flags trending issues before they become RAG red. Stops surprises landing at gate reviews.' },
                { icon: FileCheck2, t: 'Evidence generation', d: 'Compiles structured evidence packs as work happens. No last-minute audit scramble.' },
                { icon: Target,t: 'Decision-ready output', d: 'Every chart, every figure, every flag traceable to its source. Trust by transparency.' },
              ].map((f) => (
                <div key={f.t} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-base font-medium text-slate-900">{f.t}</p>
                    <p className="text-sm text-slate-600 mt-0.5">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="relative bg-slate-900 rounded-2xl p-2.5 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.4)] ring-1 ring-slate-900/5">
              <img
                src="/images/healthcare/hc-pmo.jpg"
                alt="AI-assisted PMO Command Centre — Videra Healthcare"
                className="w-full aspect-[4/3] object-contain object-center bg-slate-50 rounded-lg"
              />
            </div>
            <p className="mt-3 text-xs text-slate-500 text-center">
              Videra PMO Command Centre — portfolio health, predictive risk and gate queue at a glance.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ============= WHAT IT DOES FOR YOU — 6 OUTCOMES ============= */}
    <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="v2-platform-outcomes">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">What it does for you</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 max-w-3xl mb-12">
          One environment. <span className="text-slate-400">Six outcomes.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {outcomes.map((o, i) => (
            <motion.div
              key={o.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-900 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center mb-4">
                <o.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-base font-medium text-slate-900 mb-2">{o.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{o.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ============= CAPABILITIES — 7 cards ============= */}
    <section className="py-20 md:py-28" data-testid="v2-platform-capabilities">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Core capabilities</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
            Seven capabilities. <span className="text-slate-400">One environment.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {capabilities.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="bg-white border border-slate-200 rounded-xl p-7 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] transition-all"
            >
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
                  <c.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">{c.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                </div>
              </div>
              <div className="mt-6">
                <ImagePlaceholder
                  code={c.code}
                  ext="jpg"
                  alt={`${c.title} — Videra Healthcare`}
                  aspect="5 / 2"
                  brief={`${c.title} — real Videra Healthcare screen`}
                  spec="1500×600 · JPG"
                  rounded="rounded-lg"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ============= DAY 1 / DAY 30 / DAY 90 TIMELINE ============= */}
    <section className="py-20 md:py-28" data-testid="v2-platform-timeline">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4 inline-flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" /> What you get — Day 1, Day 30, Day 90
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
            Value on day one. <span className="text-slate-400">Embedded by day ninety.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {timeline.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={`rounded-2xl p-7 ${i === 1 ? 'bg-slate-900 text-white border border-slate-800' : 'bg-white border border-slate-200'}`}
            >
              <p className={`text-xs uppercase tracking-[0.2em] font-semibold mb-3 ${i === 1 ? 'text-blue-400' : 'text-blue-600'}`}>{t.label}</p>
              <h3 className={`text-2xl font-medium mb-3 ${i === 1 ? 'text-white' : 'text-slate-900'}`}>{t.title}</h3>
              <p className={`text-sm leading-relaxed mb-5 ${i === 1 ? 'text-slate-300' : 'text-slate-600'}`}>{t.desc}</p>
              <ul className="space-y-2">
                {t.items.map((x) => (
                  <li key={x} className={`flex items-start gap-2 text-sm ${i === 1 ? 'text-slate-200' : 'text-slate-700'}`}>
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${i === 1 ? 'text-emerald-400' : 'text-emerald-500'}`} />
                    {x}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ============= COMPARISON TABLE (moved here from home) ============= */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-platform-comparison">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">An honest comparison</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
            How Videra compares.
          </h2>
          <p className="mt-4 text-slate-600">
            Other tools solve isolated functions. Videra is the coordinating layer that brings everything together.
          </p>
        </div>
        <ComparisonTable />
      </div>
    </section>

    {/* ============= VIDEO 4 removed — single intro video lives on home ============= */}

    {/* ============= REAL SCREENS — selected from Videra Healthcare ============= */}
    <section className="py-20 md:py-28 bg-white" data-testid="v2-platform-real-screens">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
              Real screens, real workflows
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              From the <span className="text-slate-400">Videra Healthcare</span> demo.
            </h2>
            <p className="mt-4 text-slate-600">
              These are not mock-ups. Each shot is a live screen from the FM Hard Services workspace
              — the same code paths used by our paying clients.
            </p>
          </div>
          <Link
            to="/sectors/hard-services"
            className="inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors group"
            data-testid="v2-platform-real-screens-link"
          >
            Open the full demo tour
            <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {[
            { f: 'hc-projects-list.jpg', label: 'Projects list', sub: 'Stage, status, RAG, budget — every project on one page.' },
            { f: 'hc-projects-gates-closed.jpg', label: 'CO — Construction gate', sub: 'Gate Progress + Stage Checklist + Deliverables, auto-validated.' },
            { f: 'hc-rfis.jpg', label: 'RFIs register', sub: 'Open, responded, overdue — with SLA timers and project links.' },
            { f: 'hc-health-safety-approvals.jpg', label: 'H&S approvals', sub: 'Live approval queue, status, owner, last review timestamp.' },
            { f: 'hc-asset-register.jpg', label: 'Asset register', sub: 'Condition, residual life, links to projects, RAMS and certificates.' },
            { f: 'hc-portfolio-tracker-snapshot.jpg', label: 'Project snapshot', sub: 'Burn, variance, slippage, gate dwell, open governance — inline.' },
          ].map((it) => (
            <Link
              key={it.f}
              to="/sectors/hard-services"
              className="group block bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-lg transition-all"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={`/images/healthcare/${it.f}`}
                  alt={it.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <div className="p-4 md:p-5">
                <p className="text-sm font-medium text-slate-900">{it.label}</p>
                <p className="mt-1 text-[12px] md:text-[13px] text-slate-600 leading-snug">{it.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ============= REAL SCREENS — selected from Videra Healthcare ============= */}
    <section className="py-20 md:py-28 bg-white" data-testid="v2-platform-real-screens">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
              Real screens, real workflows
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              From the <span className="text-slate-400">Videra Healthcare</span> demo.
            </h2>
            <p className="mt-4 text-slate-600">
              These are not mock-ups. Each shot is a live screen from the FM Hard Services workspace
              — the same code paths used by our paying clients.
            </p>
          </div>
          <Link
            to="/sectors/hard-services"
            className="inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors group"
            data-testid="v2-platform-real-screens-link"
          >
            Open the full demo tour
            <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {[
            { f: 'hc-projects-list.jpg', label: 'Projects list', sub: 'Stage, status, RAG, budget — every project on one page.' },
            { f: 'hc-projects-gates-closed.jpg', label: 'CO — Construction gate', sub: 'Gate Progress + Stage Checklist + Deliverables, auto-validated.' },
            { f: 'hc-rfis.jpg', label: 'RFIs register', sub: 'Open, responded, overdue — with SLA timers and project links.' },
            { f: 'hc-health-safety-approvals.jpg', label: 'H&S approvals', sub: 'Live approval queue, status, owner, last review timestamp.' },
            { f: 'hc-asset-register.jpg', label: 'Asset register', sub: 'Condition, residual life, links to projects, RAMS and certificates.' },
            { f: 'hc-portfolio-tracker-snapshot.jpg', label: 'Project snapshot', sub: 'Burn, variance, slippage, gate dwell, open governance — inline.' },
          ].map((it) => (
            <Link
              key={it.f}
              to="/sectors/hard-services"
              className="group block bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-lg transition-all"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={`/images/healthcare/${it.f}`}
                  alt={it.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <div className="p-4 md:p-5">
                <p className="text-sm font-medium text-slate-900">{it.label}</p>
                <p className="mt-1 text-[12px] md:text-[13px] text-slate-600 leading-snug">{it.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ============= REAL SCREENS — selected from Videra NHS Estates ============= */}
    <section className="py-20 md:py-28 bg-slate-50 border-t border-slate-200" data-testid="v2-platform-real-screens-nhs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
              Real screens, real workflows
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              From the <span className="text-slate-400">Videra NHS Capital Programme</span> demo.
            </h2>
            <p className="mt-4 text-slate-600">
              Board-grade reporting, RIBA-staged delivery and synchronised RIBA · KSAR · HAI-SCRIBE
              compliance — purpose-built for NHS Trust capital teams.
            </p>
          </div>
          <Link
            to="/sectors/nhs"
            className="inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors group"
            data-testid="v2-platform-real-screens-nhs-link"
          >
            Open the full NHS demo tour
            <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {[
            { f: 'nhs-portfolio-tracker.jpg', label: 'NHS Portfolio Tracker', sub: '57 schemes, £721.3M, RAG mix and sector spend rollup on one page.' },
            { f: 'nhs-board-pack.jpg', label: 'Trust Board Pack — Quarterly', sub: 'Board-grade capital programme position statement — one-click PDF.' },
            { f: 'nhs-pmo-command-centre.jpg', label: 'PMO Command Centre', sub: 'Single pane of glass — RIBA stage, HBN/HTM compliance, RAG and PM capacity.' },
            { f: 'nhs-compliance-rails.jpg', label: 'RIBA · KSAR · HAI-SCRIBE', sub: 'Three-rail compliance, synchronised — required deliverables auto-checked.' },
            { f: 'nhs-project-riba-stages.jpg', label: 'NHS Project — RIBA Plan of Work', sub: 'S0 → S7 with gate-doc sign-off, budget, AI analyse and Assurance Pack.' },
            { f: 'nhs-financial-systems.jpg', label: 'NHS Finance Integrations', sub: 'Native SBS Finance, Oracle EBS / Fusion Cloud and Unit4 ERP connectors.' },
          ].map((it) => (
            <Link
              key={it.f}
              to="/sectors/nhs"
              className="group block bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-lg transition-all"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={`/images/nhs/${it.f}`}
                  alt={it.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <div className="p-4 md:p-5">
                <p className="text-sm font-medium text-slate-900">{it.label}</p>
                <p className="mt-1 text-[12px] md:text-[13px] text-slate-600 leading-snug">{it.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ============= REAL SCREENS — selected from Videra Construction+ ============= */}
    <section className="py-20 md:py-28 bg-white border-t border-slate-200" data-testid="v2-platform-real-screens-cp">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
              Real screens, real workflows
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              From the <span className="text-slate-400">Videra Construction+</span> demo.
            </h2>
            <p className="mt-4 text-slate-600">
              Tier-1 main-contractor delivery on the CPlus 7 / RIBA Plan of Work — board-grade
              reporting, lifecycle planning and Building Safety Act 2022-ready governance, multi-tenant from day one.
            </p>
          </div>
          <Link
            to="/sectors/construction"
            className="inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors group"
            data-testid="v2-platform-real-screens-cp-link"
          >
            Open the full Construction+ demo tour
            <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {[
            { f: 'cp-portfolio-pmo.jpg', label: 'PMO — Portfolio Command Centre', sub: 'KPIs, heat-map, forward gate calendar, RACI matrix and top-of-risk projects.' },
            { f: 'cp-live-portfolio.jpg', label: 'Live portfolio', sub: '52 projects across the CPlus 7 framework — stage, framework, value, spend %, RAG.' },
            { f: 'cp-project-stages.jpg', label: 'Project — RIBA Plan of Work', sub: 'Stage 4 TEC · gate-approval pending · Golden-Thread sign-off ready.' },
            { f: 'cp-lifecycle.jpg', label: 'Lifecycle — 5-Yr Plan Builder', sub: '1,825 assets · 995 BIM-linked · £430.3m replacement value, generated automatically.' },
            { f: 'cp-safety-rams.jpg', label: 'Safety, RAMS & audits', sub: 'Portfolio-wide RAMS register with SafetyAI weekly brief feeding the board pack.' },
            { f: 'cp-bid-pipeline.jpg', label: 'Bid pipeline & BidAI', sub: '£728m active pipeline · 50% win rate · BidAI-drafted narratives anchored in won work.' },
          ].map((it) => (
            <Link
              key={it.f}
              to="/sectors/construction"
              className="group block bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-lg transition-all"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={`/images/construction/${it.f}`}
                  alt={it.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <div className="p-4 md:p-5">
                <p className="text-sm font-medium text-slate-900">{it.label}</p>
                <p className="mt-1 text-[12px] md:text-[13px] text-slate-600 leading-snug">{it.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ============= FINAL CTA ============= */}
    <section className="py-24 md:py-32 bg-slate-950 text-white relative overflow-hidden" data-testid="v2-platform-cta">
      <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
          See Videra on your portfolio.
        </h2>
        <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto">
          Spin up the live environment with sample NHS / FM / Construction data, or book a tailored
          walkthrough with the team.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="https://demo.videra.app"
            data-testid="v2-platform-cta-final-trial"
            className="inline-flex items-center justify-center bg-white text-slate-900 px-8 py-4 font-medium tracking-wide hover:bg-slate-100 transition-all rounded-md group"
          >
            <Sparkles className="mr-2 w-4 h-4" /> Start 14-day trial
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-transparent border border-white/30 text-white px-8 py-4 font-medium tracking-wide hover:bg-white/10 transition-all rounded-md"
          >
            <Play className="mr-2 w-4 h-4" /> Book a guided demo
          </Link>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          No card · build up to 3 of your own sites · ↗ opens in a new tab
        </p>
      </div>
    </section>
  </div>
);

export default V2Platform;
